import * as React from 'react';
import { Layout } from 'react-admin';
import { Container, AppBar, Toolbar, Typography, IconButton, Box, Menu, MenuItem, Button, Drawer, Stack } from '@mui/material/';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme } from '../../components/themes/main_theme.js';
import '../../index.css';
import Footer from './Footer.tsx';

/**
 * Navigation.
 *
 * Labels and paths are declared together — the old version derived the path
 * from the label with `.replace(' ', '_')`, which only replaces the first
 * space and forced every label to match its route spelling.
 *
 * Donate is deliberately separated from the rest as a filled button: on a
 * ministry site it is the primary action, not just another nav item.
 */
const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about_us' },
  { label: 'Ministries', path: '/ministries' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Blog', path: '/blog' },
  { label: 'News', path: '/news' },
  { label: 'Contact', path: '/contact_us' },
];

const DONATE = { label: 'Donate', path: '/donate' };

export const CustomLayout = (props: any) => {
  return (
    <Layout {...props} sidebar={MySidebar} appBar={CustomAppBar}>
      {props.children}
      <Footer />
    </Layout>
  );
};

export const MySidebar = () => (
  <Drawer variant="temporary" open={false} />
);

export const CustomAppBar = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleNav = (path: string) => {
    setAnchorElNav(null);
    navigate(path);
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <ThemeProvider theme={lightTheme}>
      <AppBar color="default" elevation={0}>
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 72 } }}>
            {/* Mobile: hamburger on the left */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="Open navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                edge="start"
              >
                <MenuIcon sx={{ color: 'primary.main' }} />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                keepMounted
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  '& .MuiPaper-root': { width: '100vw', maxWidth: '100vw' },
                }}
              >
                {[...NAV_ITEMS, DONATE].map((item) => (
                  <MenuItem key={item.path} onClick={() => handleNav(item.path)} selected={isActive(item.path)}>
                    <Typography color="primary" sx={{ fontWeight: isActive(item.path) ? 700 : 500 }}>
                      {item.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Brand — full name on desktop, short mark on phones */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              onClick={() => navigate('/')}
              sx={{
                cursor: 'pointer',
                flexGrow: { xs: 1, md: 0 },
                justifyContent: { xs: 'center', md: 'flex-start' },
                mr: { md: 3 },
              }}
            >
              <LightModeIcon sx={{ color: 'primary.main' }} />
              <Typography
                variant="h6"
                noWrap
                sx={{ display: { xs: 'none', lg: 'block' }, fontWeight: 700, color: 'primary.main' }}
              >
                Salt and Light Ministries
              </Typography>
              <Typography
                variant="h6"
                noWrap
                sx={{ display: { xs: 'block', lg: 'none' }, fontWeight: 900, letterSpacing: '.2rem', color: 'primary.main' }}
              >
                SALM
              </Typography>
            </Stack>

            {/* Desktop links */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 0.5 }}>
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  sx={{
                    px: 1.75,
                    color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive(item.path) ? 700 : 500,
                    '&:hover': { color: 'primary.main', backgroundColor: 'rgba(138,62,44,0.06)' },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="contained"
                color="secondary"
                startIcon={<FavoriteIcon />}
                onClick={() => handleNav(DONATE.path)}
                sx={{ ml: 1.5, px: 2.5, py: 0.9 }}
              >
                {DONATE.label}
              </Button>
            </Box>

            {/* Phone: compact donate action so giving is always one tap away */}
            <IconButton
              aria-label="Donate"
              onClick={() => handleNav(DONATE.path)}
              sx={{ display: { xs: 'flex', md: 'none' }, color: 'secondary.main' }}
            >
              <FavoriteIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>
    </ThemeProvider>
  );
};
